import React from 'react';
import Link from 'next/link';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTheme } from '@material-ui/core/styles';
import { Button, Grid, Hidden } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import clsx from 'clsx';

import { closeModal } from 'features/utilsSlice';
import Info from './Info';
import useModalOpen from 'hooks/useModalOpen';
import InfluencerNetworks from './InfluencerNetworks';
import WorkingStats from './WorkingStats';
import InventoryTabs from './InventoryTabs';
import ProfileCover from 'components/profile/ProfileCover';
import ProfileInfo from 'components/profile/ProfileInfo';
import TransitionDialog from 'components/TransitionDialog';
import Bio from 'components/Bio';
import styles from 'styles/components/profile/Profile.module.scss';

const maxHeight = { height: 'unset', paddingBottom: 40 };
const padding = { paddingBottom: 40 };
const marginTop = { marginTop: 10 };
const alertStyle = { opacity: 1, backgroundColor: 'rgba(235, 237, 242, 0.6)' };

const removeDuplicates = array =>
  array.filter((el, index) => index === array.findIndex(item => item.id === el.id));

const useStyles = makeStyles({
  paperFullScreenDialog: {
    width: '100%',
    height: '80%',
    marginTop: 'auto',
  },
});

const greyBackground = { backgroundColor: '#f8f9fb' };

const ProfilePreviewDialog = ({ profile, setProfile }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector(state => state.auth.currentUser);
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));
  const open = useModalOpen('Profile Preview');
  const classes = useStyles();

  const handleClose = () => dispatch(closeModal('Profile Preview'));

  const inventories = profile.inventories.filter(inventory => inventory.active);

  const influencerNetworks = inventories.map(inventory => inventory.influencer_network);

  const uniqInfluencerNetworks = removeDuplicates(influencerNetworks);

  const networks = inventories
    .map(i => ({
      space: i.inventory_space.name,
      network: i.influencer_network.network,
    }))
    .reduce((prev, obj) => {
      const availableSpaces = prev[obj.network.id]?.availableSpaces || [];

      prev[obj.network.id] = {
        ...obj.network,
        availableSpaces: [...availableSpaces, obj.space],
      };

      return prev;
    }, {});

  return (
    <TransitionDialog
      direction="up"
      desktopDirection="up"
      open={open}
      handleClose={handleClose}
      dialogStyles={classes.paperFullScreenDialog}
      styles={styles}
      whiteExit
    >
      <div
        className={clsx(styles.profileContainer, {
          [styles.maxHeight]: profile.inventories.length === 0,
        })}
        style={profile.inventories.length === 0 ? maxHeight : padding}
      >
        <ProfileCover setProfile={setProfile} profile={profile} profilePreview />

        <ProfileInfo
          setProfile={setProfile}
          profile={profile}
          uniqInfluencerNetworks={uniqInfluencerNetworks}
          profilePreview
        />

        <Hidden mdUp>
          <WorkingStats working_stats={profile.working_stats} />
        </Hidden>

        {uniqInfluencerNetworks.length > 0 && (
          <Hidden mdUp>
            <InfluencerNetworks
              profile={profile}
              influencerNetworks={uniqInfluencerNetworks}
              profilePreview
            />
          </Hidden>
        )}

        {profile?.bio && (
          <Hidden mdUp>
            <Bio profile={profile} setProfile={setProfile} profilePreview />
          </Hidden>
        )}

        <Grid container className={styles.filterContainer} style={mobile ? greyBackground : {}}>
          <>
            {inventories.length === 0 ? (
              currentUser.role === 'influencer' ? (
                <Alert severity="info" className={styles.alert} style={alertStyle}>
                  <span style={{ opacity: 0.6 }}>
                    You have no networks set up. Please fill in your inventory by clicking the
                    button below.
                  </span>
                  <br />
                  <Link href="/inventory" passHref>
                    <Button variant="contained" color="primary" style={marginTop}>
                      Set up your Inventory
                    </Button>
                  </Link>
                </Alert>
              ) : (
                <Alert severity="info" className={styles.alert}>
                  This influencer has not finished setting up their inventory yet.
                </Alert>
              )
            ) : (
              <>
                <Hidden smDown>
                  <Grid item md={4}>
                    <Info profile={profile} setProfile={setProfile} profilePreview />

                    <WorkingStats working_stats={profile.working_stats} noPaddingLeft />
                  </Grid>
                </Hidden>

                <Grid item xs={12} md={8}>
                  <InventoryTabs
                    preview
                    uniqInfluencerNetworks={uniqInfluencerNetworks}
                    inventories={inventories}
                    networks={networks}
                    profile={profile}
                  />
                </Grid>
              </>
            )}
          </>
        </Grid>
      </div>
    </TransitionDialog>
  );
};

ProfilePreviewDialog.propTypes = {
  setProfile: PropTypes.func.isRequired,
  profile: PropTypes.object.isRequired,
};

export default ProfilePreviewDialog;
